<template>
  <div class="pending-review-page">
    <div class="query-card">
      <div class="filter-row">
        <div class="filter-item">
          <label>📅 年度</label>
          <el-select
            v-model="filters.year"
            placeholder="请选择年度"
            style="width: 100%"
          >
            <el-option label="全部" value="all" />
            <el-option
              v-for="item in yearOptions"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
        </div>
        <div class="filter-item">
          <label>👤 申请人部门</label>
          <el-select
            v-model="filters.dept"
            placeholder="请选择部门"
            style="width: 100%"
            v-loading="deptLoading"
          >
            <el-option label="全部部门" value="all" />
            <el-option
              v-for="item in deptOptions"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
        </div>
        <div class="action-buttons">
          <el-button type="primary" icon="el-icon-search" @click="handleQuery"
            >查询</el-button
          >
          <el-button icon="el-icon-refresh" @click="handleReset"
            >重置</el-button
          >
        </div>
      </div>
    </div>

    <div class="table-wrapper">
      <el-table
        :data="tableData"
        border
        style="width: 100%"
        :header-cell-style="{
          background: '#f8fafc',
          color: '#1f4e43',
          fontWeight: '600',
        }"
        v-loading="loading"
      >
        <el-table-column prop="userName" label="申请人" width="100" />
        <el-table-column prop="departmentName" label="部门" min-width="140" />
        <el-table-column prop="periodDetail" label="考核周期" min-width="140" />
        <el-table-column prop="assessmentType" label="考核类型" width="120" />
        <el-table-column
          prop="assessmentScore"
          label="考核得分"
          width="100"
          align="center"
        >
          <template slot-scope="scope">
            <strong>{{ scope.row.finalScore }}</strong>
          </template>
        </el-table-column>
        <el-table-column prop="fillDate" label="申请时间" width="120" />
        <el-table-column
          prop="processStatus"
          label="当前状态"
          width="150"
          align="center"
        >
          <template slot-scope="scope">
            <span :class="['status-badge', getStatusClass(scope.row.status)]">
              {{ scope.row.processStatus }}
            </span>
          </template>
        </el-table-column>
        <el-table-column label="打分" width="250" align="center">
          <template slot-scope="scope">
            <div
              v-if="scope.row.processStatus === '待部门领导审核'"
              class="action-group"
            >
              <el-button
                type="primary"
                size="small"
                @click="handleSubmitScore(scope.row, 2)"
              >
                ✓ 去审核
              </el-button>
            </div>

            <div
              v-if="scope.row.processStatus === '待总经理复核'"
              class="action-group"
            >
              <el-button
                type="primary"
                size="small"
                @click="handleSubmitScore(scope.row, 3)"
              >
                ✓ 去复核
              </el-button>
            </div>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页组件 -->
      <div class="pagination-container">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="pagination.pageNum"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="pagination.pageSize"
          layout="total, sizes, prev, pager, next, jumper"
          :total="pagination.total"
        />
      </div>
    </div>

    <div class="footer-note">
      <span>
        打分操作：为待审核人员输入考核得分，确认后提交评分与审核意见。</span
      >
    </div>

    <!-- 审核打分弹框 -->
    <review-dialog
      :visible.sync="reviewDialogVisible"
      :row-data="currentReviewRow"
      :step="step"
      @submit="handleReviewSubmit"
      @close="handleReviewClose"
      @query="handleDialogQuery"
    />
  </div>
</template>

<script>
import {
  getDepartments,
  getAssessments,
  reviewSubmit,
} from '@/API/apply/apply.js';
import ReviewDialog from './ReviewDialog.vue';

export default {
  name: 'PendingReview',
  components: {
    ReviewDialog,
  },
  props: {
    dataList: {
      type: Array,
      required: true,
    },
    filters: {
      type: Object,
      required: true,
    },
    pagination: {
      type: Object,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      tableData: [],
      yearOptions: [],
      deptOptions: [],
      deptLoading: false,
      reviewDialogVisible: false,
      currentReviewRow: {},
      step: 2,
    };
  },
  computed: {
    // 移除前端的 filteredData，改为直接使用传入的 dataList
  },
  watch: {
    dataList: {
      handler(newVal) {
        this.tableData = newVal || [];
        console.log('tableData:', this.tableData);
      },
      immediate: true,
    },
  },
  mounted() {
    this.generateYearOptions();
    this.loadDeptOptions();
  },
  methods: {
    // 加载部门选项
    loadDeptOptions() {
      // 从 localStorage 获取公司ID
      const userDetail = JSON.parse(
        localStorage.getItem('ordinaryUser'),
      )?.userDetail;
      const companyId = userDetail?.companyInfo?.companyId;

      if (!companyId) {
        console.warn('未获取到公司ID');
        return;
      }

      this.deptLoading = true;

      getDepartments(companyId)
        .then((res) => {
          if (res.code === 200) {
            this.deptOptions = res.data.map((item) => ({
              value: item.id,
              label: item.deptName,
            }));
          } else {
            console.error('获取部门列表失败:', res.message);
          }
        })
        .catch((error) => {
          console.error('获取部门列表失败:', error);
        })
        .finally(() => {
          this.deptLoading = false;
        });
    },
    generateYearOptions() {
      const currentYear = new Date().getFullYear();
      this.yearOptions = [];

      // 获取当前年度和过去5年（包含当前年）
      // 例如：当前是2026年，则显示 2026, 2025, 2024, 2023, 2022, 2021
      for (let i = 0; i < 6; i++) {
        const year = currentYear - i;
        this.yearOptions.push({
          value: `${year}`,
          label: `${year}年`,
        });
      }
    },
    handleQuery() {
      // 触发父组件的查询事件，重置页码为1
      this.$emit('query');
    },
    handleReset() {
      // 重置筛选条件
      this.$emit('reset');
    },
    handleSubmitScore(row, step) {
      this.handleDialogDetails(row.id, step);
    },
    handleDialogDetails(id, step) {
      getAssessments({ id: id }, id).then((res) => {
        this.currentReviewRow = res.data;
        this.step = step;
        this.reviewDialogVisible = true;
      });
    },
    handleDialogQuery() {
      // 接收子组件 ReviewDialog 的 query 事件，并向上传递给父组件 performance.vue
      this.$emit('query');
    },
    handleReviewSubmit(submitData) {
      reviewSubmit(submitData, submitData.id)
        .then((res) => {
          if (res.code === 200) {
            this.$message.success(res.message || '审核完成，已提交上级复核');
            // 提交成功后关闭弹框
            this.reviewDialogVisible = false;
            this.currentReviewRow = {};
            // 通知父组件刷新待我审核页面数据
            this.$emit('query');
          } else {
            this.$message.error(res.message || '审核提交失败');
          }
        })
        .catch((error) => {
          console.error('审核提交失败:', error);
          this.$message.error('审核提交失败，请重试');
        });
    },
    handleReviewClose() {
      this.currentReviewRow = {};
    },
    handleSizeChange(val) {
      // 改变每页条数
      this.$emit('size-change', val);
    },
    handleCurrentChange(val) {
      // 改变当前页
      this.$emit('current-change', val);
    },
    getStatusClass(status) {
      const classMap = {
        审核通过: 'status-approved',
        审核不通过: 'status-rejected',
        待部门领导审核: 'status-pending',
        待总经理复核: 'status-pending',
      };
      return classMap[status] || '';
    },
  },
};
</script>

<style scoped lang="less">
.pending-review-page {
  animation: fadeIn 0.2s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.query-card {
  background: #f9fbfd;
  border-radius: 20px;
  padding: 20px 24px;
  margin-bottom: 24px;
  border: 1px solid #eef2f8;
}

.filter-row {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 18px;
  row-gap: 18px;
}

.filter-item {
  display: flex;
  flex-direction: column;
  min-width: 140px;
  flex: 1;

  label {
    font-size: 12px;
    font-weight: 500;
    color: #3c5a6b;
    margin-bottom: 6px;
    letter-spacing: 0.3px;
  }
}

.action-buttons {
  display: flex;
  gap: 12px;
  margin-left: auto;
  align-items: center;
}

.table-wrapper {
  overflow-x: auto;
  border-radius: 18px;
  //border: 1px solid #ecf1f7;
  background: white;
}

.pagination-container {
  padding: 20px;
  display: flex;
  justify-content: flex-end;
  border-top: 1px solid #ecf1f7;
}

.status-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 40px;
  font-size: 11px;
  font-weight: 500;
}

.status-pending {
  background: #ffedd5;
  color: #b45309;
}

.status-approved {
  background: #dff9e6;
  color: #1e6f5c;
}

.status-rejected {
  background: #fee2e2;
  color: #b91c1c;
}

.status-draft {
  background: #e6f0fa;
  color: #2c5282;
}

.action-group {
  display: flex;
  gap: 8px;
  justify-content: center;
  align-items: center;
}

.footer-note {
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #e9edf2;
  font-size: 12px;
  color: #6b8a9b;
  text-align: right;
}

/deep/ .el-input-number {
  .el-input__inner {
    text-align: center;
  }
}
</style>
